import React from "react";
import { Handle, Position } from "@xyflow/react";
import { useTranslation } from "react-i18next";
import { HoverTooltip } from "../../common/HoverTooltip";
import TechnologyIcon from "../../common/TechnologyIcon";
import { isEarthRangerTypeValue } from "../../destinations/earthRangerUrl";
import { RAIL_IN_X, RAIL_OUT_X } from "./useFlowGraph";

// The card's height is fixed and mirrored by NODE_H, which React Flow is given as
// the node's declared height: the handles below sit at 35%/65% of it, so a card
// with a URL line and one without still connect at the same heights, and the map
// never has to wait for a measurement to show a node.
const AccountNode = ({ data }: any) => {
    const { t } = useTranslation();
    return (
        <HoverTooltip content={data.needsSetup ? t("Configure Integration — setup needed") : t("Configure Integration")} position="top" portal focusable={false} className="block">
        <div data-testid={`AccountNode-${data.isProvider ? "provider" : "destination"}-div`}
            className={`border rounded-lg bg-white px-4 py-3 w-[260px] h-[100px] cursor-pointer transition-shadow hover:shadow-md ${
            data.isSelected ? "border-green-1 ring-2 ring-green-1" : "border-unlisted-gray-lines"}`}>
            <div className="flex items-center gap-2">
                {/* Logo where there is one, first letter where there isn't — same
                    box either way, so every card keeps the same footprint. */}
                <TechnologyIcon
                    typeValue={data.techValue}
                    typeName={data.techName}
                    boxClassName="w-7 h-7 text-sm"
                    imgClassName="w-7 h-7 object-contain" />
                <span className="text-ui-text-primary-text font-semibold truncate" title={data.name}>{data.name}</span>
                {/* checklist state: every required field saved (✓) or setup pending (!) */}
                <span className={`ml-auto flex-none w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center ${
                    data.needsSetup ? "bg-ui-status-bg-error-bg text-ui-status-error-destructive border border-ui-status-error-destructive" : "bg-teal-t10 text-green-1"}`}
                    aria-label={data.needsSetup ? t("Needs setup — required fields are missing") : t("Configured")}>
                    {data.needsSetup ? "!" : "✓"}
                </span>
            </div>
            {/* The address only says something for EarthRanger, where it names the site;
                for every other technology it is an internal endpoint. */}
            {data.base_url && isEarthRangerTypeValue(data.techValue) && <div className="text-xs text-ui-text-primary-text truncate mt-1" title={data.base_url}>{data.base_url}</div>}
            {data.techName && (
                <div className="mt-1.5">
                    {/* Truncates rather than wraps — the card height stays fixed. */}
                    <span className="block truncate text-[11px] uppercase tracking-wider text-ui-text-primary-text font-semibold" title={data.techName}>
                        {data.techName}
                    </span>
                </div>
            )}

            {data.isProvider ? (
                <>
                    {data.vertical ? (
                        <>
                            <Handle type="source" id="out-source" position={Position.Bottom} isConnectable={false}
                                className="opacity-0" style={{ left: RAIL_OUT_X }} />
                            <Handle type="target" id="in-target" position={Position.Bottom} isConnectable={false}
                                className="opacity-0" style={{ left: RAIL_IN_X }} />
                        </>
                    ) : (
                        <>
                            <Handle type="source" id="out-source" position={Position.Right} isConnectable={false}
                                className="opacity-0" style={{ top: "35%" }} />
                            <Handle type="target" id="in-target" position={Position.Right} isConnectable={false}
                                className="opacity-0" style={{ top: "65%" }} />
                        </>
                    )}
                </>
            ) : (
                <>
                    <Handle type="target" id="out-target" position={Position.Left} isConnectable={false}
                        className="opacity-0" style={{ top: "35%" }} />
                    <Handle type="source" id="in-source" position={Position.Left} isConnectable={false}
                        className="opacity-0" style={{ top: "65%" }} />
                </>
            )}
        </div>
        </HoverTooltip>
    );
};

export default AccountNode;
